import { z } from "zod";
import { orderItemSchema } from "./orderItem";

// ── Base fields shared across all order types ──
const baseOrderFields = z.object({
  discount_percentage: z.number().min(0).max(100).default(0),
  discount_value: z.number().min(0).default(0),
  /** Modo do desconto global no pedido (não confundir com discount_type por item) */
  global_discount_type: z.enum(["percent", "fixed"]).nullable().optional(),
  freight_value: z.number().min(0).default(0),
  notes: z.string().optional(),
});

// ── Sale base without items ──
const saleBaseRequired = z.object({
  customer_id: z.string().min(1, "Cliente é obrigatório"),
  salesperson_id: z.string().optional(),
  sale_date: z.date(),
  delivery_date: z.date().optional(),
  delivery_address: z.string().optional(),
});

const saleBaseOptionalCustomer = z.object({
  customer_id: z.string().optional(),
  salesperson_id: z.string().optional(),
  sale_date: z.date(),
  delivery_date: z.date().optional(),
  delivery_address: z.string().optional(),
});

const itemsField = z.object({
  items: z.array(orderItemSchema).min(1, "Adicione pelo menos um item"),
});

const incomeCategoryField = z.object({
  income_category_id: z.string().min(1, "Categoria financeira é obrigatória"),
});

// ── Pre-built Sale Form Schemas ──

/** NewSaleDialog: customer required, no items in schema (validated manually) */
export const saleFormSchemaNewDialog = saleBaseRequired.merge(baseOrderFields);

const incomeCategoryOptionalField = z.object({
  income_category_id: z.string().uuid().nullable().optional(),
});

/** EditSaleDialog: customer required, items in schema, income_category optional */
export const saleFormSchemaEditDialog = saleBaseRequired
  .merge(baseOrderFields)
  .merge(itemsField)
  .merge(incomeCategoryOptionalField);

/** EditSale (full page): customer optional, items + income_category in schema.
 *  income_category_id é opcional no Zod — validação condicional por permissão
 *  feita no onSubmit do EditSale (quem tem sales:edit_financial_category vê o campo). */
export const saleFormSchemaEditPage = saleBaseOptionalCustomer
  .merge(baseOrderFields)
  .merge(itemsField)
  .merge(incomeCategoryOptionalField)
  .merge(z.object({ fiscal_notes: z.string().optional() }));

// ── Quotation Schemas ──

const quotationBase = z.object({
  customer_id: z.string().min(1, "Cliente é obrigatório"),
  quotation_date: z.date(),
  valid_until: z.date(),
});

const quotationStatusField = z.object({
  status: z.enum(["draft", "sent", "approved", "rejected", "expired", "converted"]),
});

const quotationConditionsLegacy = z.object({
  payment_conditions: z.string().optional(),
  delivery_conditions: z.string().optional(),
});

const quotationConditionsNew = z.object({
  payment_condition_id: z.string().optional(),
  delivery_deadline: z.string().optional(),
  fiscal_notes: z.string().optional(),
});

/** NewQuotationDialog: no status, no items, legacy conditions */
export const quotationFormSchemaNewDialog = quotationBase
  .merge(baseOrderFields)
  .merge(quotationConditionsLegacy);

/** EditQuotationDialog: with status, items, legacy conditions */
export const quotationFormSchemaEditDialog = quotationBase
  .merge(baseOrderFields)
  .merge(quotationStatusField)
  .merge(quotationConditionsLegacy)
  .merge(itemsField);

/** EditQuotation (full page): with status, new conditions, no items in schema */
export const quotationFormSchemaEditPage = quotationBase
  .merge(baseOrderFields)
  .merge(quotationStatusField)
  .merge(quotationConditionsNew);

// ── Unified Order Form Schema ──

export const unifiedOrderFormSchema = quotationBase
  .merge(baseOrderFields)
  .merge(quotationConditionsNew);
